import { useCallback, useEffect, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

/** Deterministic first-run setup walkthrough — scripted steps, never LLM-generated. */
const STEPS = [
  { anchor: null, title: "Welcome to MISE",
    body: "I'm MISE Intelligence — the bubble in the corner. Two minutes now saves hours later: let me show you the setup loop." },
  { anchor: "nav-suppliers", title: "1 · Add your suppliers",
    body: "Supplier Directory is where your suppliers live. Add one, then use 'Import price list' on its row — MISE reads the PDF, spreadsheet or photo and pre-builds product matching for you." },
  { anchor: "nav-stock", title: "2 · Capture invoices",
    body: "Stock Items & Review is where supplier invoices land. Upload a photo or PDF, review the matched lines, confirm — stock costs update themselves and price drift gets flagged." },
  { anchor: "nav-recipe-book", title: "3 · Build your recipes",
    body: "Recipe Book turns stock items into dishes with live cost and margin %. Until real invoices post, MISE shows amber 'est' prices from your supplier catalogs so you can price dishes immediately." },
  { anchor: "nav-dashboard", title: "4 · Watch the numbers",
    body: "Dashboard carries revenue, covers, spend per head and your daily brief. Ask me anything at any time — the bubble stays in the corner on every screen." },
  { anchor: null, title: "You're set",
    body: "That's the loop: suppliers → invoices → recipes → dashboard. The bubble answers how-to questions and runs daily/weekly/monthly reports whenever you ask." },
];

export function Tour() {
  const { user } = useAuth();
  const [step, setStep] = useState(-1);
  const isMgr = user && ["owner", "manager"].includes(user.role);

  useEffect(() => {
    if (isMgr && !user.onboarded_at) setStep(0);
  }, [isMgr, user?.onboarded_at]);

  useEffect(() => {
    const h = () => setStep(0);
    window.addEventListener("mise:start-tour", h);
    return () => window.removeEventListener("mise:start-tour", h);
  }, []);

  const finish = useCallback(async () => {
    setStep(-1);
    try { await api.post("/assistant/onboarding-done"); } catch { /* retry next login */ }
  }, []);

  if (step < 0 || step >= STEPS.length) return null;
  const s = STEPS[step];
  const rect = s.anchor && typeof document !== "undefined"
    ? document.querySelector(`[data-testid="${s.anchor}"]`)?.getBoundingClientRect() : null;
  const last = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[90]" data-testid="tour-overlay">
      <div className="absolute inset-0 bg-black/60" />
      {rect && (
        <div className="fixed rounded-md border-2 border-primary pointer-events-none z-[95] animate-pulse"
          style={{ left: rect.left - 4, top: rect.top - 4, width: rect.width + 8, height: rect.height + 8 }}
          data-testid="tour-highlight" />
      )}
      <div
        className="fixed z-[95] w-[min(360px,calc(100vw-2rem))] rounded-xl border border-border p-4 space-y-3 shadow-2xl"
        style={rect
          ? { background: "#151226", left: Math.min(rect.right + 16, window.innerWidth - 380), top: Math.max(16, rect.top - 8) }
          : { background: "#151226", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
        data-testid="tour-card">
        <p className="font-heading font-semibold flex items-center gap-2" data-testid="tour-title">
          <Sparkle size={16} className="text-primary" /> {s.title}
        </p>
        <p className="text-sm text-muted-foreground" data-testid="tour-body">{s.body}</p>
        <div className="flex items-center justify-between pt-1">
          <Button variant="ghost" size="sm" data-testid="tour-skip" onClick={finish}>Skip tour</Button>
          <div className="flex gap-2">
            {step > 0 && <Button variant="outline" size="sm" data-testid="tour-back" onClick={() => setStep(step - 1)}>Back</Button>}
            <Button size="sm" data-testid="tour-next" className="bg-primary hover:bg-[#8B5CF6] text-white"
              onClick={() => (last ? finish() : setStep(step + 1))}>
              {last ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
